"use client";

import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useJotFormCredential } from "@/hooks/useJotFormCredential";
import JotFormCallout from "./JotFormCallout";
import JotFormPinnedFormsSection from "./JotFormPinnedFormsSection";
import JotFormSecretSection from "./JotFormSecretSection";
import JotFormWebhookUrlsSection from "./JotFormWebhookUrlsSection";

interface JotFormSetupPanelProps {
  /** Lets the marketplace card re-read its connected badge. */
  onChanged?: () => void;
}

export default function JotFormSetupPanel({
  onChanged,
}: Readonly<JotFormSetupPanelProps>) {
  const { t } = useTranslation("partners");
  const { t: ct } = useTranslation("common");
  const {
    status,
    pins,
    updatedAt,
    webhookTargets,
    webhookTargetsTruncated,
    webhookTargetsStatus,
    revealedSecret,
    isIssuing,
    isSavingPins,
    isDisconnecting,
    load,
    issue,
    savePins,
    disconnect,
    clearRevealedSecret,
    loadWebhookTargets,
  } = useJotFormCredential();

  const handleIssueReplacement = useCallback(async () => {
    const succeeded = await issue();
    if (succeeded) onChanged?.();
  }, [issue, onChanged]);

  if (status === "loading") {
    return (
      <div className="space-y-3" data-testid="jotform-setup-skeleton">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (status === "loadError") {
    return (
      <div className="space-y-3" data-testid="jotform-setup-load-error">
        <p className="text-sm text-500">{t("jotformIntegration.loadError")}</p>
        <Button type="button" size="sm" variant="outline" onClick={load}>
          {ct("retry")}
        </Button>
      </div>
    );
  }

  if (status === "unreadable") {
    return (
      <JotFormCallout
        tone="red"
        message={t("jotformIntegration.credentialUnreadable")}
        testId="jotform-setup-unreadable"
      >
        <Button
          type="button"
          size="sm"
          onClick={handleIssueReplacement}
          disabled={isIssuing}
        >
          {t("jotformIntegration.issueNewSecret")}
        </Button>
      </JotFormCallout>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="jotform-setup-panel">
      <JotFormSecretSection
        issued={status === "issued"}
        updatedAt={updatedAt}
        revealedSecret={revealedSecret}
        isIssuing={isIssuing}
        isDisconnecting={isDisconnecting}
        onIssue={issue}
        onDisconnect={disconnect}
        onDismissSecret={clearRevealedSecret}
        onChanged={onChanged}
      />
      <JotFormPinnedFormsSection
        pins={pins}
        disabled={status !== "issued"}
        isSaving={isSavingPins}
        onSave={savePins}
      />
      <JotFormWebhookUrlsSection
        state={webhookTargetsStatus}
        targets={webhookTargets}
        truncated={webhookTargetsTruncated}
        onRetry={loadWebhookTargets}
      />
    </div>
  );
}
